/**
 * The one place EVERY TanStack query key under the playbooks namespace is
 * built (plans/2026-09-25-002-playbooks-play-isolation-lean-plan.md, Key
 * Technical Decision 15, U5; the full-namespace sweep is plan
 * 2026-09-27-006 U6). Unscoped (`playId` omitted/undefined) is byte-identical
 * to the key every caller used before play isolation — `playbookDetailQueryKey(id)`
 * / `libraryQueryKey()`. Scoped (`playId` given) appends `{ playId }` as a
 * fourth/second element — used only inside the play flow, once a read
 * carries `?playId=` (Decision 12).
 *
 * Prefix invalidations still work: `invalidateQueries({ queryKey:
 * playbookDetailQueryKey(playbookId) })` matches both the unscoped AND any
 * scoped entry for that playbook, because TanStack's default `exact: false`
 * match is a positional-prefix match and the scoped key's leading elements
 * are identical to the unscoped key's. No call site that invalidates by
 * that prefix needs to change.
 *
 * `sequenceDetailQueryKey` takes no `playId`: a play-owned sequence is its
 * own identity (Decision 12, revert of the overnight U4 scoping — U5), so
 * `GET /sequences/:id` never varies by play and the cache entry never needs
 * to either.
 *
 * `PLAYBOOKS_NS` (a shared const, not a hand-typed `'playbooks'` string
 * literal repeated in every function body) is what lets
 * `__tests__/module-graph.test.ts` pin "no file under `mini-apps/playbooks/`
 * builds a playbooks-namespaced query key by hand" as a SOURCE-TEXT regex
 * without also flagging the one file that legitimately builds these keys —
 * this one. Every exported function's return type spells the namespace as
 * `typeof PLAYBOOKS_NS`, never the bare string literal, for the same reason.
 *
 * `playsPrefixKey()`/`matchedMembersPrefixKey()` are deliberately exported
 * too: several call sites (`dispatch-inspector-logic.ts#playSendsQueryKey`,
 * `SendLogRow.tsx#sentCopyQueryKey`,
 * `MemberTestDrawer.tsx`'s dispatch-payload preview) need a play-id-scoped
 * key nested under the plays prefix that this file has no dedicated helper
 * for — they spread the prefix rather than hand-roll the literal.
 * `sentCopyQueryKey` also folds the row's own `retry_count` into its key
 * (U9, 2026-09-30), so a retry's `sends` refetch busts the open sent-copy
 * pane's cache too instead of leaving it showing the refused attempt's copy.
 *
 * Deliberately ZERO imports (pinned by `__tests__/module-graph.test.ts`):
 * this file is a pure leaf importable from anywhere in the app — components,
 * hooks, the fetch layer — with no risk of dragging in a heavier module
 * graph (the same failure mode that file's other pins guard against).
 */

const PLAYBOOKS_NS = 'playbooks' as const;

/** The one, never-scoped sequence-detail key — a play-owned sequence is its own identity. */
export function sequenceDetailQueryKey(sequenceId: string): readonly [typeof PLAYBOOKS_NS, 'sequence', string] {
  return [PLAYBOOKS_NS, 'sequence', sequenceId];
}

/** The one, never-scoped sequence-usage key. */
export function sequenceUsageQueryKey(
  sequenceId: string,
): readonly [typeof PLAYBOOKS_NS, 'sequence-usage', string] {
  return [PLAYBOOKS_NS, 'sequence-usage', sequenceId];
}

/** The one, never-scoped segmentation-detail key. */
export function segmentationDetailQueryKey(
  segmentationId: string,
): readonly [typeof PLAYBOOKS_NS, 'segmentation', string] {
  return [PLAYBOOKS_NS, 'segmentation', segmentationId];
}

/** The one, never-scoped segmentation-usage key. */
export function segmentationUsageQueryKey(
  segmentationId: string,
): readonly [typeof PLAYBOOKS_NS, 'segmentation-usage', string] {
  return [PLAYBOOKS_NS, 'segmentation-usage', segmentationId];
}

/** The unscoped (template) form — what every caller used before play isolation. */
export type UnscopedPlaybookDetailQueryKey = readonly [typeof PLAYBOOKS_NS, 'playbook', string];

/** The play-scoped form — used only once a `playId` is known. */
export type ScopedPlaybookDetailQueryKey = readonly [
  typeof PLAYBOOKS_NS,
  'playbook',
  string,
  { readonly playId: string },
];

export type PlaybookDetailQueryKey = UnscopedPlaybookDetailQueryKey | ScopedPlaybookDetailQueryKey;

export function playbookDetailQueryKey(playbookId: string, playId?: string): PlaybookDetailQueryKey {
  if (playId === undefined) {
    return [PLAYBOOKS_NS, 'playbook', playbookId];
  }
  return [PLAYBOOKS_NS, 'playbook', playbookId, { playId }];
}

/** The unscoped (template) form — what every caller used before play isolation. */
export type UnscopedLibraryQueryKey = readonly [typeof PLAYBOOKS_NS, 'library'];

/** The play-scoped form — used only once a `playId` is known. */
export type ScopedLibraryQueryKey = readonly [typeof PLAYBOOKS_NS, 'library', { readonly playId: string }];

export type LibraryQueryKey = UnscopedLibraryQueryKey | ScopedLibraryQueryKey;

export function libraryQueryKey(playId?: string): LibraryQueryKey {
  if (playId === undefined) {
    return [PLAYBOOKS_NS, 'library'];
  }
  return [PLAYBOOKS_NS, 'library', { playId }];
}

/**
 * The Library page's plays read (`use-plays-library.ts#PLAYS_LIBRARY_QUERY_KEY`).
 * Deliberately NOT a member of the `playsPrefixKey()` family below — its own
 * doc explains why: so the campaign-scoped play lists and this one can be
 * invalidated independently, and `ReviewStep`'s broad `playsPrefixKey()`
 * invalidation after a send does not refetch the whole Library by accident.
 */
export function playsLibraryQueryKey(): readonly [typeof PLAYBOOKS_NS, 'plays-library'] {
  return [PLAYBOOKS_NS, 'plays-library'];
}

/**
 * The broad plays prefix — every campaign-scoped (`playsQueryKey`) and
 * play-id-scoped (sends, dispatch-payload preview, dispatch-log sent-copy)
 * key is built by spreading this, so one
 * `invalidateQueries({ queryKey: playsPrefixKey() })` (`ReviewStep`'s
 * post-send call) reaches all of them via TanStack's prefix match.
 */
export function playsPrefixKey(): readonly [typeof PLAYBOOKS_NS, 'plays'] {
  return [PLAYBOOKS_NS, 'plays'];
}

/** The campaign-scoped plays list (`usePlaysForCampaign`/`usePlaysLibrary`'s campaign sibling). */
export function playsQueryKey(campaignSfdcId: string): readonly [typeof PLAYBOOKS_NS, 'plays', string] {
  return [...playsPrefixKey(), campaignSfdcId];
}

/**
 * The broad matched-members prefix every matched-member read
 * (`matchedMembersLadderKey`/`filterMatchedMembersKey` in
 * `use-matched-members.ts`) is built on, so one invalidate/remove reaches
 * both the ladder and filter families.
 */
export function matchedMembersPrefixKey(): readonly [typeof PLAYBOOKS_NS, 'matched-members'] {
  return [PLAYBOOKS_NS, 'matched-members'];
}

/** The bound Gong flow's reply-step ordinals (`use-gong-reply-step-orders.ts`), keyed by flow, not sequence. */
export function gongFlowStepsQueryKey(
  flowId: string | null,
): readonly [typeof PLAYBOOKS_NS, 'gong-flow-steps', string | null] {
  return [PLAYBOOKS_NS, 'gong-flow-steps', flowId];
}

/** The Gong flow catalog browse read (`SequencesStep.tsx`'s locked-flow-name lookup). */
export function gongFlowsBrowseQueryKey(): readonly [typeof PLAYBOOKS_NS, 'gong-flows', 'browse'] {
  return [PLAYBOOKS_NS, 'gong-flows', 'browse'];
}

/** The admin Settings page's own read (`PlaybooksSettingsPage.tsx`). */
export function settingsQueryKey(): readonly [typeof PLAYBOOKS_NS, 'settings'] {
  return [PLAYBOOKS_NS, 'settings'];
}

/** "Generate with AI"'s generation-context read, keyed by sequence AND campaign. */
export function generationContextQueryKey(
  sequenceId: string,
  campaignSfdcId: string | null,
): readonly [typeof PLAYBOOKS_NS, 'generation-context', string, string | null] {
  return [PLAYBOOKS_NS, 'generation-context', sequenceId, campaignSfdcId];
}
